import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Combo de dos fajas en la ficha de producto.
 *
 * El ahorro que se muestra aquí es informativo: quien lo aplica de verdad es el
 * descuento automático de Shopify sobre el carrito. Por eso el porcentaje llega
 * desde el schema y nunca se toca el precio de los items al agregarlos.
 */
class EclipseBundle extends HTMLElement {
  #items = [];

  connectedCallback() {
    this.#items = Array.from(this.querySelectorAll('[data-ec-bundle-item]'));
    if (this.#items.length < 2) return;

    this.discount = Number(this.dataset.discountPercent) || 0;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.currency = this.dataset.currency || 'USD';

    this.submitButton = this.querySelector('[data-ec-bundle-submit]');
    this.errorNode = this.querySelector('[data-ec-bundle-error]');

    this.addEventListener('change', this.#handleChange);
    this.submitButton?.addEventListener('click', this.#handleSubmit);

    // La primera fila es el producto de la página: si la clienta cambia la talla
    // arriba, el combo la sigue en vez de contradecirla.
    document.addEventListener('variant:update', this.#handleVariantUpdate);

    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    this.submitButton?.removeEventListener('click', this.#handleSubmit);
    document.removeEventListener('variant:update', this.#handleVariantUpdate);
  }

  #handleChange = (event) => {
    if (!event.target.matches('[data-ec-bundle-size]')) return;
    this.#clearError();
    this.#render();
  };

  #handleVariantUpdate = (event) => {
    const variantId = event.detail?.resource?.id;
    const item = this.#items[0];
    if (!variantId || !item) return;

    const input = item.querySelector(`[data-ec-bundle-size][value="${CSS.escape(String(variantId))}"]`);

    // Si la talla no está en esta fila, el evento es de otro producto (por
    // ejemplo una ficha combinada) y no nos toca.
    if (!input) return;

    if (input.disabled) {
      // La ficha se movió a una talla agotada. Soltamos la selección para que el
      // dúo se deshabilite en vez de arrastrar en silencio la talla anterior.
      item.querySelectorAll('[data-ec-bundle-size]').forEach((radio) => {
        radio.checked = false;
      });
      this.#render();
      return;
    }

    if (input.checked) return;

    input.checked = true;
    this.#render();
  };

  #money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  /** @returns {{item: HTMLElement, variantId: string|null, cents: number}[]} */
  #state() {
    return this.#items.map((item) => {
      const input = item.querySelector('[data-ec-bundle-size]:checked');
      return { item, variantId: input?.value || null, cents: Number(input?.dataset.price) || 0 };
    });
  }

  #render() {
    const state = this.#state();
    const chosen = state.filter((entry) => entry.variantId);
    const complete = chosen.length === this.#items.length;

    // Mientras falte una talla mostramos el precio de lista completo, para que el
    // ahorro no aparezca calculado sobre medio combo.
    const subtotal = complete
      ? state.reduce((sum, entry) => sum + entry.cents, 0)
      : this.#items.reduce((sum, item) => sum + (Number(item.dataset.basePrice) || 0), 0);

    const saving = Math.round((subtotal * this.discount) / 100);
    const total = subtotal - saving;

    this.#setText('[data-ec-bundle-subtotal]', this.#money(subtotal));
    this.#setText('[data-ec-bundle-saving]', `−${this.#money(saving)}`);
    this.#setText('[data-ec-bundle-total]', this.#money(total));

    if (!this.submitButton) return;

    const label = this.submitButton.querySelector('[data-ec-bundle-submit-label]');
    this.submitButton.disabled = !complete;

    if (label) {
      if (complete) {
        label.textContent = this.dataset.labelAdd || 'Agregar el dúo';
      } else {
        const pending = state.find((entry) => !entry.variantId);
        const prefix = this.dataset.labelPick || 'Elige la talla de';
        // Una fila sin selector visible (la que refleja la ficha) explica el
        // bloqueo con su propio texto en vez de pedir algo que no se puede elegir.
        label.textContent =
          pending?.item.dataset.pendingLabel ||
          `${prefix} ${pending?.item.dataset.productTitle || ''}`.trim();
      }
    }
  }

  #setText(selector, value) {
    const node = this.querySelector(selector);
    if (node) node.textContent = value;
  }

  #showError(message) {
    if (!this.errorNode) return;
    this.errorNode.textContent = message;
    this.errorNode.hidden = false;
  }

  #clearError() {
    if (!this.errorNode) return;
    this.errorNode.hidden = true;
    this.errorNode.textContent = '';
  }

  #handleSubmit = async (event) => {
    event.preventDefault();

    const state = this.#state();
    if (state.some((entry) => !entry.variantId)) return;

    this.#clearError();
    this.submitButton.setAttribute('aria-busy', 'true');
    this.submitButton.disabled = true;

    // Pedimos el re-render de las secciones del carrito para que el drawer no
    // quede mostrando el estado anterior.
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean);

    const cartAddUrl = (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: state.map((entry) => ({ id: Number(entry.variantId), quantity: 1 })),
          sections: sectionIds.join(','),
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload.status) {
        this.#showError(payload.description || payload.message || this.dataset.labelError || '');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent(payload, this.id, {
          source: 'eclipse-bundle',
          itemCount: state.length,
          productId: this.#items[0]?.dataset.productId,
          sections: payload.sections,
        })
      );

      window.Shopify?.analytics?.publish?.('eclipse_bundle_add', {
        items: state.length,
        discount_percent: this.discount,
      });
    } catch (error) {
      console.error(error);
      this.#showError(this.dataset.labelError || '');
    } finally {
      this.submitButton.removeAttribute('aria-busy');
      this.#render();
    }
  };
}

if (!customElements.get('eclipse-bundle')) {
  customElements.define('eclipse-bundle', EclipseBundle);
}
